import { EFinancingCompany, EFinancingProgram } from '@if/core/types'
import { apiClient } from '@src/services/http-common'
import { EDisbursementTarget } from '@src/types/constants'
import { useQuery, type UseQueryResult } from '@tanstack/react-query'

const SCOPE = 'pendingLenderPayments'

export const pendingLenderPaymentQueryKeys = {
  all: [SCOPE] as const,
}

// One row of GET {financingCompanyId}/disbursements/lender-payments/pending: a Planned lender payment not yet
// assigned to a bank job.
export interface PendingLenderPaymentDto {
  operationId: number
  loanId: number
  financingProgramId: EFinancingProgram
  contractNumber: string
  /** The borrower the loan names. Never empty: a loan always has a customer. */
  borrowerFullName: string
  /** The Monolith debourse's own description, shown verbatim. Null on an LMS-native payment. */
  description: string | null
  /** True when the payment pays out an account-to-account closing payment rather than disbursing the loan. */
  isAccountToAccountPayout: boolean
  /** True when the payment pays out an account-to-account closing whose closing is not finalized. */
  isPendingClosingFinalization: boolean
  payeeId: string
  /** The merchant's name. Null for a borrower payee, or when it could not be resolved. */
  payeeName: string | null
  payeeType: EDisbursementTarget
  /**
   * What the operation carries, as stored: always positive, an account-to-account payout included.
   * The screen negates the latter (signedAmountOf), so formatCurrency prints it in parentheses and
   * the totals subtract it.
   */
  amount: number
  paymentOn: string // DateOnly as string from API
}

export interface PendingLenderPaymentsParams {
  financingCompanyId: EFinancingCompany
  searchTerm: string
  offset: number
  limit: number
}

const buildSearchParams = ({ searchTerm, offset, limit }: PendingLenderPaymentsParams) => {
  const params = new URLSearchParams({
    offset: String(offset),
    limit: String(limit),
  })
  if (searchTerm) params.set('searchTerm', searchTerm)
  return params
}

const getPendingLenderPayments = async (params: PendingLenderPaymentsParams): Promise<PendingLenderPaymentDto[]> => {
  // The company is a path segment, not a query parameter: CompanyAccessMiddleware reads it off the
  // route to check the caller belongs to it before the endpoint runs.
  const company = encodeURIComponent(params.financingCompanyId)
  const url = `${company}/disbursements/lender-payments/pending?${buildSearchParams(params).toString()}`
  const response = await apiClient.get<PendingLenderPaymentDto[]>(url)
  return response.data
}

export function useGetPendingLenderPayments(
  params: PendingLenderPaymentsParams,
): UseQueryResult<PendingLenderPaymentDto[]> {
  return useQuery({
    queryKey: [...pendingLenderPaymentQueryKeys.all, params],
    queryFn: () => getPendingLenderPayments(params),
  })
}
